import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import {
  getShowDiscountTypes,
  updateDiscountTypesData,
} from "@/Api/discountTypes";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        const { data: responseApi } = await getShowDiscountTypes(uuid);
        form.setValue("name", responseApi?.response?.name || "");
      }
    };

    fetchData();
  }, [isOpen, uuid, form]);

  const handleOpenUpdate = () => {
    setIsOpen(true);
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      ...data,
    };

    const { data: apiResponse, ok } = await updateDiscountTypesData(
      uuid,
      payload
    );
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront(apiResponse?.response);
    toast({
      description:
        apiResponse?.message || "Tipo de desconto atualizado com sucesso!",
      action: <Check color="#1edc2b" />,
    });

    setFormState("default");
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={handleOpenUpdate}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Tipo de Desconto
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form
          onSubmit={form.handleSubmit(formSubmit)}
          {...form}
          className="space-y-3"
        >
          <Form.Row>
            <Form.Field name="name">
              {({ field }) => (
                <Form.Item>
                  <Form.Label>
                    Nome <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <Input {...field} placeholder="Insira um nome" />
                  </Form.Control>
                </Form.Item>
              )}
            </Form.Field>
          </Form.Row>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Atualizando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Atualizar"}
              {formState === "error" && "Erro ao atualizar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
